import { useEffect, useMemo, useState } from 'react';
import { NumberField, TextField } from '../../components/form';
import CurrencySelect from '../../components/form/currencySelect';
import ToggleButton from '../../components/common/toggle';
import { post } from '../../libs/api';
import { Errors, Successes } from '../../components/common/msg';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { groupInteger } from '../../libs/numberFormat';

const formatCurrencyCode = (value, currency = 'USD') =>
  `${currency} ${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0)}`;

const DEFAULT_CONFIG = {
  fixingFrequency: '',
  numberOfPeriods: '',
  spotReference: '',
  strikePercent: '',
  koTrigger: '',
  noKoPeriods: '',
  buyAmount1: '',
  buyAmount2: '',
  boostedMultiplier: '',
  upfrontFee: '',
};

const getValue = (obj, keys, fallback) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return fallback;
};

const toNumber = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }
  const parsed = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : fallback;
};

const floorToDecimals = (value, decimals) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  const factor = 10 ** decimals;
  return Math.floor(num * factor) / factor;
};

const extractCalcData = (response) => {
  return (
    response?.data?.data ||
    response?.data?.result ||
    response?.data ||
    response?.result ||
    {}
  );
};

const extractFixingDateRows = (response) => {
  const candidates = [
    response?.data?.fixing_settlement_dates,
    response?.data?.data,
    response?.data?.list,
    response?.data?.rows,
    response?.data,
    response?.result?.data,
    response?.result?.list,
    response?.result,
  ];
  for (const item of candidates) {
    if (Array.isArray(item)) {
      return item;
    }
  }
  return [];
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const addMonths = (date, months) => {
  const next = new Date(date);
  next.setUTCMonth(next.getUTCMonth() + months);
  return next;
};

const adjustForWeekend = (date) => {
  const next = new Date(date);
  const day = next.getUTCDay(); // 0=Sun, 6=Sat
  if (day === 6) next.setUTCDate(next.getUTCDate() + 2); // Sat → Mon
  if (day === 0) next.setUTCDate(next.getUTCDate() + 1); // Sun → Mon
  return next;
};

const toApiDateTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString().replace('T', ' ').replace('Z', '').slice(0, 19);
};

const extractProductsFromResponse = (response) => {
  const candidates = [
    response?.data,
    response?.data?.list,
    response?.data?.products,
    response?.data?.rows,
    response?.result,
    response?.result?.list,
    response?.result?.products,
  ];

  for (const item of candidates) {
    if (Array.isArray(item)) {
      return item;
    }
  }
  return [];
};

const formatApiDateTime = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value.toISOString().replace('T', ' ').replace('Z', '').slice(0, 19);
  }
  if (typeof value === 'number') {
    return new Date(value)
      .toISOString()
      .replace('T', ' ')
      .replace('Z', '')
      .slice(0, 19);
  }
  return String(value).replace('T', ' ').replace('Z', '').slice(0, 19);
};

const extractMaturityDate = (response) => {
  const candidates = [
    response,
    response?.data?.data,
    response?.data?.result,
    response?.data,
    response?.result?.data,
    response?.result,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' || typeof candidate === 'number') {
      return candidate;
    }
    if (Array.isArray(candidate) && candidate.length > 0) {
      const first = candidate[0];
      if (typeof first === 'string' || typeof first === 'number') {
        return first;
      }
      const fromFirst = getValue(
        first,
        [
          'maturity_date',
          'maturityDate',
          'maturity_datetime',
          'maturityDateTime',
          'maturity',
          'date',
        ],
        null
      );
      if (fromFirst) {
        return fromFirst;
      }
    }
    const maturityDate = getValue(
      candidate,
      [
        'maturity_date',
        'maturityDate',
        'maturity_datetime',
        'maturityDateTime',
        'maturity',
        'date',
        'maturitydate',
        'maturity_date_time',
      ],
      null
    );
    if (maturityDate) {
      return maturityDate;
    }
  }
  return null;
};

const mapProductToConfig = (product) => {
  const custom = getValue(
    product,
    ['custom_ebtca_product', 'customEbtcaProduct', 'custom_product'],
    {}
  );

  const rawFrequencyValue = getValue(
    { ...product, ...custom },
    [
      'fixingFrequency',
      'fixing_frequency',
      'monthly_or_weekly',
      'settlementFrequency',
      'settlement_frequency',
      'settFreq',
      'sett_freq',
      'calculation_frequency',
    ],
    DEFAULT_CONFIG.fixingFrequency
  );

  const rawFrequency = String(rawFrequencyValue || '').trim();
  const upperFrequency = rawFrequency.toUpperCase();
  const fixingFrequency = upperFrequency
    ? upperFrequency.includes('MONTH') || Number(rawFrequencyValue) >= 28
      ? 'Monthly'
      : 'Weekly'
    : '';

  const strikePercent = toNumber(
    getValue(
      { ...product, ...custom },
      ['strikePercent', 'strike_percent', 'strike', 'Strike'],
      DEFAULT_CONFIG.strikePercent
    ),
    DEFAULT_CONFIG.strikePercent
  );

  const koTrigger = toNumber(
    getValue(
      { ...product, ...custom },
      ['koPercent', 'ko_percent', 'koTrigger', 'ko_trigger', 'Ko_trigger'],
      DEFAULT_CONFIG.koTrigger
    ),
    DEFAULT_CONFIG.koTrigger
  );

  const spotReference = toNumber(
    getValue(
      { ...product, ...custom },
      ['spotReference', 'spot_reference', 'referencePrice'],
      DEFAULT_CONFIG.spotReference
    ),
    DEFAULT_CONFIG.spotReference
  );

  return {
    fixingFrequency,
    numberOfPeriods: toNumber(
      getValue(
        { ...product, ...custom },
        [
          'numberOfPeriods',
          'number_of_periods',
          'tenor',
          'calculationPeriod',
          'calculation_period',
        ],
        DEFAULT_CONFIG.numberOfPeriods
      ),
      DEFAULT_CONFIG.numberOfPeriods
    ),
    spotReference: spotReference || DEFAULT_CONFIG.spotReference,
    strikePercent,
    koTrigger,
    noKoPeriods: toNumber(
      getValue(
        { ...product, ...custom },
        [
          'noKoPeriods',
          'no_ko_periods',
          'no_of_ko_periods',
          'No of Ko periods',
        ],
        DEFAULT_CONFIG.noKoPeriods
      ),
      DEFAULT_CONFIG.noKoPeriods
    ),
    buyAmount1: toNumber(
      getValue(
        { ...product, ...custom },
        ['buyAmount1', 'buy_amount_1'],
        DEFAULT_CONFIG.buyAmount1
      ),
      DEFAULT_CONFIG.buyAmount1
    ),
    buyAmount2: toNumber(
      getValue(
        { ...product, ...custom },
        ['buyAmount2', 'buy_amount_2'],
        DEFAULT_CONFIG.buyAmount2
      ),
      DEFAULT_CONFIG.buyAmount2
    ),
    boostedMultiplier: toNumber(
      getValue(
        { ...product, ...custom },
        [
          'boostedMultiplier',
          'boosted_multiplier',
          'multiplier_2x',
          'multiplier2x',
        ],
        DEFAULT_CONFIG.boostedMultiplier
      ),
      DEFAULT_CONFIG.boostedMultiplier
    ),
    upfrontFee: toNumber(
      getValue(
        { ...product, ...custom },
        ['upfrontFee', 'upfront_fee', 'Upfront fee'],
        DEFAULT_CONFIG.upfrontFee
      ),
      DEFAULT_CONFIG.upfrontFee
    ),
    investmentAmount: toNumber(
      getValue(
        { ...product, ...custom },
        ['investmentAmount', 'investment_amount', 'min_amount'],
        DEFAULT_CONFIG.investmentAmount
      ),
      DEFAULT_CONFIG.investmentAmount
    ),
  };
};

const Component = () => {
  const { sourceCurrency, targetCurrency } = useParams();
  const location = useLocation();
  const [investmentCC, setInvestmentCC] = useState(
    String(sourceCurrency || 'USDC').toUpperCase()
  );
  const [notionalCurrency, setNotionalCurrency] = useState(
    String(targetCurrency || 'USDC').toUpperCase()
  );
  const [spotReferenceCC, setSpotReferenceCC] = useState(
    `${
      String(sourceCurrency || 'BTC')
        .toUpperCase()
        .includes('ETH')
        ? 'ETH'
        : 'BTC'
    }/${String(targetCurrency || 'USDC').toUpperCase()}`
  );
  const [buzzerPercent, setBuzzerPercent] = useState('');
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [form, setForm] = useState({
    investmentAmount: DEFAULT_CONFIG.investmentAmount,
    referenceRate: '1.0',
    notionalAmount: '',
    fixingFrequency: DEFAULT_CONFIG.fixingFrequency,
    numberOfPeriods: DEFAULT_CONFIG.numberOfPeriods,
    spotReference: DEFAULT_CONFIG.spotReference,
    strikePercent: DEFAULT_CONFIG.strikePercent,
    koTrigger: DEFAULT_CONFIG.koTrigger,
    noKoPeriods: DEFAULT_CONFIG.noKoPeriods,
    boostedMultiplier: DEFAULT_CONFIG.boostedMultiplier,
  });
  const [productMeta, setProductMeta] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [errors, setErrors] = useState([]);
  const [successes, setSuccesses] = useState([]);
  const [initialForm, setInitialForm] = useState(null);
  const [calcResponse, setCalcResponse] = useState(null);
  const [calcRunId, setCalcRunId] = useState(0);
  const [maturityDateFromApi, setMaturityDateFromApi] = useState(null);
  const [fixingDateRows, setFixingDateRows] = useState([]);
  const [fixingDateLoading, setFixingDateLoading] = useState(false);
  const [fixingPage, setFixingPage] = useState(1);
  const [isBuzzerOn, setIsBuzzerOn] = useState(false);
  const [strikePriceInput, setStrikePriceInput] = useState('');
  const [koPriceInput, setKoPriceInput] = useState('');
  const [buzzerAmount, setBuzzerAmount] = useState('');
  const navigate = useNavigate();
  const routeProductTitle = location?.state?.productTitle;
  const routeProductType = String(location?.state?.productType || 'btc')
    .toLowerCase()
    .trim();
  const baseSpotAsset = routeProductType.includes('eth') ? 'ETH' : 'BTC';
  const spotReferenceOptions = useMemo(
    () => [
      { value: `${baseSpotAsset}/USDT`, label: `${baseSpotAsset}/USDT` },
      { value: `${baseSpotAsset}/USDC`, label: `${baseSpotAsset}/USDC` },
    ],
    [baseSpotAsset]
  );
  const pageProductTitle = useMemo(() => {
    return (
      routeProductTitle ||
      productMeta?.display_title ||
      productMeta?.product_name ||
      `BTC Accumulator (Structured Product) - ${String(
        investmentCC || sourceCurrency || 'USDT'
      ).toUpperCase()}`
    );
  }, [
    investmentCC,
    productMeta?.display_title,
    productMeta?.product_name,
    routeProductTitle,
    sourceCurrency,
  ]);
  const isSameInvestmentAndNotionalCurrency =
    String(investmentCC || '')
      .trim()
      .toUpperCase() ===
    String(notionalCurrency || '')
      .trim()
      .toUpperCase();

  const fetchProducts = async (currency, sourceCurrency) => {
    return await post('/investment/product/list', {
      currency,
      sourceCurrency,
      productType: 'accum',
    });
  };

  // Sync spot reference pair with notional currency
  useEffect(() => {
    const matchingOption = spotReferenceOptions.find(
      (opt) =>
        opt.value.endsWith(`/${notionalCurrency}`) ||
        opt.value.endsWith(`/${notionalCurrency?.toUpperCase()}`)
    );
    if (matchingOption) {
      setSpotReferenceCC(matchingOption.value);
    } else {
      const allowedValues = spotReferenceOptions.map((x) => x.value);
      if (!allowedValues.includes(spotReferenceCC)) {
        setSpotReferenceCC(
          spotReferenceOptions[0]?.value || `${baseSpotAsset}/USDC`
        );
      }
    }
  }, [baseSpotAsset, notionalCurrency, spotReferenceCC, spotReferenceOptions]);

  useEffect(() => {
    const loadEnhancedBtcAccumulatorProduct = async () => {
      setErrors([]);

      const selectedCurrency = String(investmentCC || 'USDT').toLowerCase();
      const data = await fetchProducts(selectedCurrency, selectedCurrency);

      if (data?.status !== 200) {
        setErrors([data?.error || 'Failed to load product configuration.']);
        return;
      }

      const products = extractProductsFromResponse(data);
      // if (products.length === 0) {
      //   setErrors([
      //     'No matching product. Please choose another configuration.',
      //   ]);
      //   return;
      // }

      const p = products[0] || {};
      const mapped = mapProductToConfig(p);
      const nextInvestmentAmount = String(mapped.investmentAmount ?? '');
      const nextReferenceRate = '1.0';
      const nextNotionalAmount = (() => {
        const investAmt = Number(nextInvestmentAmount) || 0;
        const refRate = Number(nextReferenceRate) || 0;
        const value = investAmt * refRate;
        return value > 0 ? String(value) : '';
      })();
      setConfig(mapped);
      setProductMeta(p);
      setForm({
        investmentAmount: nextInvestmentAmount,
        referenceRate: nextReferenceRate,
        notionalAmount: nextNotionalAmount,
        fixingFrequency: mapped.fixingFrequency,
        numberOfPeriods: String(mapped.numberOfPeriods ?? ''),
        spotReference: String(mapped.spotReference ?? ''),
        strikePercent: String(mapped.strikePercent ?? ''),
        koTrigger: String(mapped.koTrigger ?? ''),
        noKoPeriods: String(mapped.noKoPeriods ?? ''),
        boostedMultiplier: String(mapped.boostedMultiplier ?? ''),
      });
      // Initialize price inputs from loaded product data
      const spotNum = Number(mapped.spotReference) || 0;
      const strikePctNum = Number(mapped.strikePercent) || 0;
      const koTriggerNum = Number(mapped.koTrigger) || 0;
      if (spotNum > 0 && strikePctNum > 0) {
        setStrikePriceInput(
          String((spotNum * (strikePctNum / 100)).toFixed(2))
        );
      }
      if (spotNum > 0 && koTriggerNum > 0) {
        setKoPriceInput(String((spotNum * (koTriggerNum / 100)).toFixed(2)));
      }
      setInitialForm({
        fixingFrequency: String(mapped.fixingFrequency ?? ''),
        numberOfPeriods: String(mapped.numberOfPeriods ?? ''),
        strikePercent: String(mapped.strikePercent ?? ''),
        koTrigger: String(mapped.koTrigger ?? ''),
        noKoPeriods: String(mapped.noKoPeriods ?? ''),
        boostedMultiplier: String(mapped.boostedMultiplier ?? ''),
      });
    };

    loadEnhancedBtcAccumulatorProduct();
  }, [investmentCC, sourceCurrency, targetCurrency]);

  useEffect(() => {
    const loadWallet = async () => {
      const data = await post('/wallet/balance', {});
      if (data?.status !== 200 || !Array.isArray(data?.data)) {
        return;
      }
      const currentWallet = data.data.find(
        (x) => x.currency?.toUpperCase() === investmentCC?.toUpperCase()
      );
      setWallet(currentWallet || null);
    };
    loadWallet();
  }, [investmentCC]);

  useEffect(() => {
    if (!isSameInvestmentAndNotionalCurrency) {
      return;
    }
    setForm((prev) =>
      prev.referenceRate === '1.0' ? prev : { ...prev, referenceRate: '1.0' }
    );
    setFieldErrors((prev) =>
      prev.referenceRate ? { ...prev, referenceRate: '' } : prev
    );
  }, [isSameInvestmentAndNotionalCurrency]);

  const validateField = (field, value) => {
    const parsed = Number(value);
    const decimal2Pattern = /^\d+(\.\d{1,2})?$/;
    const integerPattern = /^\d+$/;

    if (
      value === '' ||
      value === null ||
      value === undefined ||
      Number.isNaN(parsed)
    ) {
      return 'This field is required';
    }
    if (field === 'noKoPeriods' && parsed < 0) {
      return 'Please enter 0 or a positive whole number';
    }
    if (field !== 'noKoPeriods' && parsed <= 0) {
      return 'Please enter a positive value';
    }

    if (
      ['investmentAmount', 'notionalAmount', 'spotReference'].includes(field)
    ) {
      if (!decimal2Pattern.test(String(value))) {
        return 'Use up to 2 decimal places';
      }
    }
    if (['strikePercent', 'koTrigger'].includes(field)) {
      if (!/^\d+(\.\d{1,6})?$/.test(String(value))) {
        return 'Use up to 6 decimal places';
      }
    }
    if (field === 'boostedMultiplier' && (parsed < 1 || parsed > 2)) {
      return 'Boosted Multiplier must be between 1 and 2';
    }
    if (field === 'numberOfPeriods' && !integerPattern.test(String(value))) {
      return 'Tenor must be a whole number';
    }
    if (field === 'noKoPeriods' && !integerPattern.test(String(value))) {
      return 'No of KO periods must be a whole number';
    }
    return '';
  };

  const setValidatedValue = (field, value, setter) => {
    setter(value);
    const error = validateField(field, value);
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleStrikePriceChange = (value) => {
    setStrikePriceInput(value);
    const strikePrice = Number(value);
    const spotNum = Number(form.spotReference);
    if (value === '' || value === null || value === undefined) {
      setValidatedValue('strikePercent', '', (v) =>
        setForm((prev) => ({ ...prev, strikePercent: v }))
      );
      return;
    }
    if (!Number.isFinite(strikePrice) || strikePrice <= 0) {
      return;
    }
    // Keep spot reference fixed, derive strike % from strike price
    if (Number.isFinite(spotNum) && spotNum > 0) {
      const strikePercent = ((strikePrice / spotNum) * 100).toFixed(6);
      setValidatedValue('strikePercent', strikePercent, (v) =>
        setForm((prev) => ({ ...prev, strikePercent: v }))
      );
    }
  };

  const handleKoPriceChange = (value) => {
    setKoPriceInput(value);
    const koPrice = Number(value);
    const spotNum = Number(form.spotReference);
    if (value === '' || value === null || value === undefined) {
      setValidatedValue('koTrigger', '', (v) =>
        setForm((prev) => ({ ...prev, koTrigger: v }))
      );
      return;
    }
    if (!Number.isFinite(koPrice) || koPrice <= 0) {
      return;
    }
    // Keep spot reference fixed, derive KO trigger % from KO price
    if (Number.isFinite(spotNum) && spotNum > 0) {
      const koTrigger = ((koPrice / spotNum) * 100).toFixed(6);
      setValidatedValue('koTrigger', koTrigger, (v) =>
        setForm((prev) => ({ ...prev, koTrigger: v }))
      );
    }
  };

  const handleBufferPriceChange = (value) => {
    setBuzzerAmount(value);
    const bufferPrice = Number(value);
    const spotNum = Number(form.spotReference);
    if (
      !Number.isFinite(bufferPrice) ||
      bufferPrice <= 0 ||
      !Number.isFinite(spotNum) ||
      spotNum <= 0
    ) {
      setBuzzerPercent('');
      return;
    }
    setBuzzerPercent(((bufferPrice / spotNum) * 100).toFixed(6));
  };

  const handleBufferPercentChange = (value) => {
    setBuzzerPercent(value);
    const bufferPercent = Number(value);
    const spotNum = Number(form.spotReference);
    if (
      !Number.isFinite(bufferPercent) ||
      bufferPercent <= 0 ||
      !Number.isFinite(spotNum) ||
      spotNum <= 0
    ) {
      setBuzzerAmount('');
      return;
    }
    setBuzzerAmount(((bufferPercent / 100) * spotNum).toFixed(2));
  };

  const handleSpotReferenceChange = (value) => {
    setValidatedValue('spotReference', value, (v) =>
      setForm((prev) => ({ ...prev, spotReference: v }))
    );

    const spot = Number(value);
    if (!Number.isFinite(spot) || spot <= 0) {
      return;
    }

    const strikePct = Number(form.strikePercent);
    if (Number.isFinite(strikePct) && strikePct > 0) {
      setStrikePriceInput(String((spot * (strikePct / 100)).toFixed(2)));
    }

    const koTriggerPct = Number(form.koTrigger);
    if (Number.isFinite(koTriggerPct) && koTriggerPct > 0) {
      setKoPriceInput(String((spot * (koTriggerPct / 100)).toFixed(2)));
    }

    const bufferPct = Number(buzzerPercent);
    if (Number.isFinite(bufferPct) && bufferPct > 0) {
      setBuzzerAmount(String((spot * (bufferPct / 100)).toFixed(2)));
    }
  };

  const handlePeriodStep = (step) => {
    const current = Number(form.numberOfPeriods) || 0;
    const next = Math.max(1, current + step);
    setValidatedValue('numberOfPeriods', String(next), (v) =>
      setForm((prev) => ({ ...prev, numberOfPeriods: v }))
    );
  };

  const calculated = useMemo(() => {
    const periodCount = Number(form.numberOfPeriods) || 0;
    const strikePercentNum = Number(form.strikePercent) || 0;
    const koTriggerNum = Number(form.koTrigger) || 0;
    const spotReferenceNum = Number(form.spotReference) || 0;
    const notionalAmountNum = Number(form.notionalAmount) || 0;
    const noKoPeriodsNum = Number(form.noKoPeriods) || 0;
    const boostedMultiplierNum = Number(form.boostedMultiplier) || 0;
    const strikePrice = parseFloat(
      (spotReferenceNum * (strikePercentNum / 100)).toFixed(2)
    );
    const koPrice = parseFloat(
      (spotReferenceNum * (koTriggerNum / 100)).toFixed(2)
    );
    // Match Excel logic:
    // Investment Per Fixing = Notional / NoFixings
    // Purchase Amount 1 = ROUNDDOWN(Investment Per Fixing / Multiplier, 2)
    // Purchase Amount 2 = ROUNDDOWN(Investment Per Fixing, 2)
    // Amt 1/2 = ROUNDDOWN(rounded Purchase Amount / Strike, 8)
    const investmentPerFixing =
      periodCount > 0 ? notionalAmountNum / periodCount : 0;
    const purchaseAmount1Raw =
      boostedMultiplierNum > 0 ? investmentPerFixing / boostedMultiplierNum : 0;
    const purchaseAmount2Raw = investmentPerFixing;
    const purchaseAmount1 =
      boostedMultiplierNum > 0 ? floorToDecimals(purchaseAmount1Raw, 2) : 0;
    const purchaseAmount2 = floorToDecimals(purchaseAmount2Raw, 2);
    const buyAmount1 =
      strikePrice > 0 ? floorToDecimals(purchaseAmount1 / strikePrice, 8) : 0;
    const buyAmount2 =
      strikePrice > 0 ? floorToDecimals(purchaseAmount2 / strikePrice, 8) : 0;
    const totalMaxQuantity = buyAmount2 * periodCount;
    const totalNotional = totalMaxQuantity * strikePrice;

    return {
      periodCount,
      noKoPeriods: noKoPeriodsNum,
      spotReference: spotReferenceNum,
      strikePrice,
      koPrice,
      purchaseAmount1,
      purchaseAmount2,
      buyAmount1,
      buyAmount2,
      totalMaxQuantity,
      totalNotional,
    };
  }, [
    form.fixingFrequency,
    form.notionalAmount,
    form.koTrigger,
    form.boostedMultiplier,
    form.noKoPeriods,
    form.numberOfPeriods,
    form.spotReference,
    form.strikePercent,
  ]);

  // Calculate maturity date as X periods from product start date based on fixing frequency
  // productMeta.startDate already accounts for the 08:00 UTC cutoff (4pm SGT)
  useEffect(() => {
    const calculationPeriod = Number(form.numberOfPeriods);
    const freq = String(form.fixingFrequency || '').trim();
    if (
      !Number.isFinite(calculationPeriod) ||
      calculationPeriod <= 0 ||
      !freq
    ) {
      setMaturityDateFromApi(null);
      return;
    }

    const startDate = new Date();
    let maturity;
    if (freq.toUpperCase().includes('MONTH')) {
      maturity = addMonths(startDate, calculationPeriod);
    } else {
      maturity = addDays(startDate, calculationPeriod * 7);
    }
    maturity = adjustForWeekend(maturity);
    // Set time to 08:00 UTC
    maturity.setUTCHours(8, 0, 0, 0);
    setMaturityDateFromApi(toApiDateTime(maturity));
  }, [form.numberOfPeriods, form.fixingFrequency]);

  // Auto-calculate Notional Amount = Investment Amount * Reference Rate
  useEffect(() => {
    const investAmt = Number(form.investmentAmount) || 0;
    const refRate = Number(form.referenceRate) || 0;
    const notional = parseFloat((investAmt * refRate).toFixed(2));
    setForm((prev) => ({
      ...prev,
      notionalAmount: notional > 0 ? String(notional) : '',
    }));
  }, [form.investmentAmount, form.referenceRate]);

  useEffect(() => {
    const strike = Number(calculated.strikePrice) || 0;
    const multiplier = Number(form.boostedMultiplier) || 0;
    const investmentAmount = Number(form.investmentAmount) || 0;
    const noOfFixings = Number(form.numberOfPeriods) || 0;
    const valueDate = formatApiDateTime(productMeta?.startDate);

    if (!valueDate) {
      setCalcResponse(null);
      return;
    }

    let cancelled = false;
    const runCalculation = async () => {
      try {
        const result = await post('/investment/accum/calculation', {
          strike,
          multiplier,
          investment_amount: investmentAmount,
          no_of_fixings: noOfFixings,
          valueDate,
        });
        if (cancelled) {
          return;
        }
        if (result?.status === 200) {
          setCalcResponse(extractCalcData(result));
        } else {
          setCalcResponse(null);
        }
      } finally {
        if (!cancelled) {
          setCalcRunId((prev) => prev + 1);
        }
      }
    };

    runCalculation();
    return () => {
      cancelled = true;
    };
  }, [
    calculated.strikePrice,
    form.boostedMultiplier,
    form.fixingFrequency,
    form.investmentAmount,
    form.noKoPeriods,
    form.numberOfPeriods,
    productMeta?.startDate,
  ]);

  // useEffect(() => {
  //   if (calcRunId === 0) {
  //     return;
  //   }

  //   const valueDate = productMeta?.startDate
  //     ? new Date(productMeta.startDate)
  //     : null;
  //   const endDate = productMeta?.endDate ? new Date(productMeta.endDate) : null;
  //   const calcMaturityRaw = getValue(
  //     calcResponse,
  //     ['maturity_date', 'maturityDate', 'maturity_datetime'],
  //     null
  //   );
  //   const calcMaturityFormatted = formatApiDateTime(calcMaturityRaw);
  //   const tenor = Number(form.numberOfPeriods);
  //   const listTenor = Number(config.numberOfPeriods);
  //   const freq = String(form.fixingFrequency || '').trim();

  //   if (!valueDate || Number.isNaN(valueDate.getTime()) || !freq) {
  //     setFixingDateRows([]);
  //     return;
  //   }

  //   let maturityDate = endDate;
  //   if (
  //     Number.isFinite(tenor) &&
  //     tenor > 0 &&
  //     (!Number.isFinite(listTenor) || tenor !== listTenor)
  //   ) {
  //     maturityDate = String(freq).toUpperCase().includes('MONTH')
  //       ? addMonths(valueDate, tenor)
  //       : addDays(valueDate, tenor * 7);
  //   }

  //   const payload = {
  //     value_datetime: toApiDateTime(valueDate),
  //     maturity_datetime: calcMaturityFormatted || toApiDateTime(maturityDate),
  //     fixing_frequency: freq,
  //   };

  //   if (!payload.value_datetime || !payload.maturity_datetime) {
  //     setFixingDateRows([]);
  //     return;
  //   }

  //   let cancelled = false;
  //   const fetchFixingDates = async () => {
  //     setFixingDateLoading(true);
  //     try {
  //       const result = await post(
  //         '/investment/fixing_dates/calculate',
  //         payload
  //       );
  //       if (cancelled) {
  //         return;
  //       }
  //       if (result?.status === 200) {
  //         const rows = extractFixingDateRows(result).map((row, index) => ({
  //           id: index + 1,
  //           fixingDate: getValue(
  //             row,
  //             [
  //               'fixing',
  //               'fixing_date',
  //               'fixingDate',
  //               'fixing_datetime',
  //               'value_datetime',
  //             ],
  //             ''
  //           ),
  //           settlementDate: getValue(
  //             row,
  //             [
  //               'settlement',
  //               'settlement_date',
  //               'settlementDate',
  //               'settlement_datetime',
  //               'maturity_datetime',
  //             ],
  //             ''
  //           ),
  //         }));
  //         setFixingDateRows(rows);
  //       } else {
  //         setFixingDateRows([]);
  //       }
  //     } finally {
  //       if (!cancelled) {
  //         setFixingDateLoading(false);
  //       }
  //     }
  //   };

  //   fetchFixingDates();
  //   return () => {
  //     cancelled = true;
  //   };
  // }, [
  //   calcRunId,
  //   form.fixingFrequency,
  //   form.numberOfPeriods,
  //   productMeta?.endDate,
  //   productMeta?.startDate,
  // ]);

  const displayNotional = useMemo(() => {
    const api = calcResponse || {};
    return {
      purchaseAmount1: calculated.purchaseAmount1,
      purchaseAmount2: calculated.purchaseAmount2,
      buyAmount1: calculated.buyAmount1,
      buyAmount2: calculated.buyAmount2,
      totalMaxQuantity: toNumber(
        getValue(
          api,
          ['totalMaxQuantity', 'total_max_quantity'],
          calculated.totalMaxQuantity
        ),
        calculated.totalMaxQuantity
      ),
      totalNotional: toNumber(
        getValue(
          api,
          ['totalNotional', 'total_notional', 'notional', 'notional_amount'],
          calculated.totalNotional
        ),
        calculated.totalNotional
      ),
      strikePrice: toNumber(
        getValue(
          api,
          ['strikePrice', 'strike_price', 'strike'],
          calculated.strikePrice
        ),
        calculated.strikePrice
      ),
      koPrice: toNumber(
        getValue(api, ['koPrice', 'ko_price'], calculated.koPrice),
        calculated.koPrice
      ),
    };
  }, [calcResponse, calculated]);

  const maturityDateDisplay = useMemo(() => {
    const raw =
      formatApiDateTime(maturityDateFromApi) ||
      (typeof maturityDateFromApi === 'string' ? maturityDateFromApi : null) ||
      formatApiDateTime(
        getValue(
          calcResponse,
          ['maturity_date', 'maturityDate', 'maturity_datetime'],
          null
        )
      ) ||
      formatApiDateTime(productMeta?.endDate) ||
      null;
    if (!raw) return '-';
    // Show date only + 08:00 UTC
    const datePart = raw.slice(0, 10);
    return `${datePart} 08:00 UTC`;
  }, [calcResponse, maturityDateFromApi, productMeta?.endDate]);

  const FIXING_PAGE_SIZE = 5;
  const totalFixingPages = Math.max(
    1,
    Math.ceil((fixingDateRows?.length || 0) / FIXING_PAGE_SIZE)
  );
  const pagedFixingRows = useMemo(() => {
    const start = (fixingPage - 1) * FIXING_PAGE_SIZE;
    return fixingDateRows.slice(start, start + FIXING_PAGE_SIZE);
  }, [fixingDateRows, fixingPage]);

  useEffect(() => {
    setFixingPage(1);
  }, [fixingDateRows]);

  const handleInvest = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSuccesses([]);

    if (submitting) {
      return;
    }
    if (!wallet?.wallet_id) {
      setErrors(['Wallet is not available for selected currency.']);
      return;
    }
    if (!productMeta?.product_id) {
      setErrors(['Product details are not loaded yet.']);
      return;
    }
    if (!form?.investmentAmount || Number(form.investmentAmount) <= 0) {
      setErrors(['Investment amount must be greater than 0.']);
      return;
    }
    if (!form?.referenceRate || Number(form.referenceRate) <= 0) {
      setErrors(['Reference rate is required.']);
      return;
    }
    if (!form?.notionalAmount || Number(form.notionalAmount) <= 0) {
      setErrors(['Notional amount is required.']);
      return;
    }
    if (
      form?.spotReference === '' ||
      form?.spotReference === null ||
      form?.spotReference === undefined
    ) {
      setFieldErrors((prev) => ({
        ...prev,
        spotReference: 'This field is required',
      }));
      setErrors(['Spot reference is required.']);
      return;
    }

    const customFlag = Boolean(
      initialForm &&
      (String(form.fixingFrequency ?? '') !==
        String(initialForm.fixingFrequency ?? '') ||
        String(form.numberOfPeriods ?? '') !==
          String(initialForm.numberOfPeriods ?? '') ||
        String(form.strikePercent ?? '') !==
          String(initialForm.strikePercent ?? '') ||
        String(form.koTrigger ?? '') !== String(initialForm.koTrigger ?? '') ||
        String(form.noKoPeriods ?? '') !==
          String(initialForm.noKoPeriods ?? '') ||
        String(form.boostedMultiplier ?? '') !==
          String(initialForm.boostedMultiplier ?? ''))
    );

    const calcMaturityRaw = getValue(
      calcResponse,
      ['maturity_date', 'maturityDate', 'maturity_datetime'],
      null
    );
    const finalMaturityDateTime =
      formatApiDateTime(maturityDateFromApi) ||
      formatApiDateTime(calcMaturityRaw) ||
      formatApiDateTime(productMeta.endDate);
    const fixingPayload = {
      value_datetime: formatApiDateTime(productMeta.startDate),
      maturity_datetime: finalMaturityDateTime,
      fixing_frequency: String(form.fixingFrequency || ''),
      deal_datetime: formatApiDateTime(productMeta.startDate),
      period_count: String(form.numberOfPeriods),
      transaction_id: '',
    };
    try {
      setSubmitting(true);
      const fixingResult = await post(
        '/investment/fixing_dates/calculate',
        fixingPayload
      );
      if (fixingResult?.status !== 200) {
        setErrors([fixingResult?.error || 'Failed to calculate fixing dates.']);
        return;
      }

      const payload = {
        wallet_id: wallet.wallet_id,
        product_id: productMeta.product_id,
        maturity_datetime: finalMaturityDateTime,
        value_datetime: formatApiDateTime(productMeta.startDate),
        invest_amount: Number(form.investmentAmount),
        invest_ccy: investmentCC.toUpperCase(),
        reference_rate: Number(form.referenceRate),
        notional_amount: Number(form.notionalAmount),
        notional_amount_ccy: String(notionalCurrency).toUpperCase(),
        accumulated_amount: '0.00',
        accumulated_amount_ccy: String(notionalCurrency).toUpperCase(),
        term_apy: null,
        term_actual_yield: null,
        term_basis: 'Upon Maturity',
        tenor: Number(form.numberOfPeriods) || 0,
        usdtReferenceRate: productMeta.usdtReferenceRate ?? null,
        usdcReferenceRate: productMeta.usdcReferenceRate ?? null,
        threshold_rate: null,
        payout_type: 'ON_MATURITY',
        new_apr: null,
        strike_price: Number(displayNotional.strikePrice),
        payout_details: [],
        custom_accum_product_flag: customFlag,
        custom_ebtca_product: {
          Strike: Number(form.strikePercent),
          ko_trigger: Number(form.koTrigger),
          ko_price: Number(displayNotional.koPrice),
          no_of_ko_periods: Number(form.noKoPeriods),
          multiplier2x: Number(form.boostedMultiplier),
          monthly_or_weekly: String(form.fixingFrequency || ''),
          spot_refernce: Number(form.spotReference),
          spot_refernce_ccy: String(spotReferenceCC || ''),
          buffer_amount:
            isBuzzerOn && buzzerAmount ? Number(buzzerAmount) : null,
          no_fixings: Number(form.numberOfPeriods) || 0,
        },
      };

      const result = await post('/investment/ebtca_order/add', payload);
      if (result?.status !== 200) {
        setErrors([result?.error || 'Failed to submit investment order.']);
        return;
      }
      setSuccesses(['Investment order submitted successfully.']);
      navigate('/portal/investment/new_order/' + result.data.transaction_id);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="row g-gs">
      <div className="col-12">
        {errors?.length > 0 && <Errors errors={errors} />}
        {successes?.length > 0 && <Successes msgs={successes} />}
      </div>
      <h2 className="nk-block-title fw-normal">
        {pageProductTitle} / Trade Entry
      </h2>
      <div className="col-lg-12">
        <section className="card card-bordered card-full">
          <div className="card-inner">
            {/* <h5 className="card-title mb-3">
              {pageProductTitle} / Trade Entry
            </h5> */}
            <div className="row g-3">
              {/* Row 1: Investment Amount (half width) */}
              <div className="col-md-6 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Investment Amount
                  </label>
                </div>
                <div className="form-control-group">
                  <div className="form-info">
                    <CurrencySelect
                      value={investmentCC}
                      onChange={(e) => setInvestmentCC(e.target.value)}
                      customCurrencyOptions={[
                        { value: 'USDT', label: 'USDT' },
                        { value: 'USDC', label: 'USDC' },
                      ]}
                    />
                  </div>
                  <NumberField
                    className="form-control bg-white"
                    value={form.investmentAmount}
                    onChange={(e) =>
                      setValidatedValue(
                        'investmentAmount',
                        e.target.value,
                        (v) =>
                          setForm((prev) => ({ ...prev, investmentAmount: v }))
                      )
                    }
                  />
                </div>
                <div className="form-note-group">
                  {wallet && (
                    <span className="form-note-alt">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setValidatedValue(
                            'investmentAmount',
                            String(wallet.main_balance ?? ''),
                            (v) =>
                              setForm((prev) => ({
                                ...prev,
                                investmentAmount: v,
                              }))
                          );
                        }}
                      >
                        Wallet Balance:{' '}
                        {formatCurrencyCode(
                          wallet.main_balance,
                          wallet.currency
                        )}
                      </a>
                      &nbsp;
                      <button
                        type="button"
                        className="btn btn-xs btn-primary"
                        onClick={() =>
                          setValidatedValue(
                            'investmentAmount',
                            String(wallet.main_balance ?? ''),
                            (v) =>
                              setForm((prev) => ({
                                ...prev,
                                investmentAmount: v,
                              }))
                          )
                        }
                      >
                        MAX
                      </button>
                    </span>
                  )}
                </div>
                {fieldErrors.investmentAmount && (
                  <small className="text-danger d-block">
                    {fieldErrors.investmentAmount}
                  </small>
                )}
              </div>
              <div className="w-100"></div>

              {/* Row 2: Reference Rate (half width) */}
              <div className="col-md-6 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Reference Rate
                  </label>
                </div>
                <NumberField
                  className={`form-control ${
                    isSameInvestmentAndNotionalCurrency ? 'bg-light' : ''
                  }`}
                  value={form.referenceRate}
                  onChange={(e) =>
                    setValidatedValue('referenceRate', e.target.value, (v) =>
                      setForm((prev) => ({ ...prev, referenceRate: v }))
                    )
                  }
                  decimalPlaces={8}
                  disabled={isSameInvestmentAndNotionalCurrency}
                />
                {fieldErrors.referenceRate && (
                  <small className="text-danger d-block">
                    {fieldErrors.referenceRate}
                  </small>
                )}
              </div>
              <div className="w-100"></div>

              {/* Row 3: Notional Amount (half width, with currency selector) */}
              <div className="col-md-6 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Notional Amount
                  </label>
                </div>
                <div className="form-control-group">
                  <div className="form-info">
                    <CurrencySelect
                      value={notionalCurrency}
                      onChange={(e) => setNotionalCurrency(e.target.value)}
                      customCurrencyOptions={[
                        { value: 'USDT', label: 'USDT' },
                        { value: 'USDC', label: 'USDC' },
                      ]}
                    />
                  </div>
                  <NumberField
                    className="form-control bg-white"
                    value={form.notionalAmount}
                    disabled={true}
                    decimalPlaces={2}
                  />
                </div>
                {fieldErrors.notionalAmount && (
                  <small className="text-danger d-block">
                    {fieldErrors.notionalAmount}
                  </small>
                )}
              </div>
              <div className="w-100"></div>

              {/* Row 4: Fixing Frequency, Number of Periods + Maturity Date, Spot Reference */}
              <div className="col-md-4 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Fixing Frequency
                  </label>
                </div>
                <select
                  className="form-control"
                  value={form.fixingFrequency}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      fixingFrequency: e.target.value,
                    }))
                  }
                >
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                </select>
              </div>
              <div className="col-md-4 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Number of Periods
                  </label>
                </div>
                <div className="input-group">
                  <NumberField
                    className="form-control"
                    value={form.numberOfPeriods}
                    onChange={(e) =>
                      setValidatedValue(
                        'numberOfPeriods',
                        e.target.value,
                        (v) =>
                          setForm((prev) => ({ ...prev, numberOfPeriods: v }))
                      )
                    }
                    min={1}
                    decimalPlaces={0}
                  />
                </div>
                {fieldErrors.numberOfPeriods && (
                  <small className="text-danger d-block">
                    {fieldErrors.numberOfPeriods}
                  </small>
                )}
                <div className="form-label-group mt-2">
                  <label className="form-label iv-invest-lb-fixed">
                    Maturity Date
                  </label>
                </div>
                <TextField
                  className="form-control"
                  value={maturityDateDisplay}
                  disabled={true}
                />
              </div>
              <div className="col-md-4 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Spot reference
                  </label>
                </div>
                <div className="form-control-group">
                  <div className="form-info">
                    <CurrencySelect
                      value={spotReferenceCC}
                      onChange={(e) => {
                        const pair = e.target.value;
                        setSpotReferenceCC(pair);
                        // Sync notional currency with spot reference denominator
                        const denom = pair.includes('/')
                          ? pair.split('/')[1]
                          : '';
                        if (denom) {
                          setNotionalCurrency(denom);
                        }
                      }}
                      customCurrencyOptions={spotReferenceOptions}
                    />
                  </div>
                  <NumberField
                    className="form-control"
                    value={form.spotReference}
                    onChange={(e) => handleSpotReferenceChange(e.target.value)}
                    min={0}
                    decimalPlaces={2}
                  />
                </div>
                {fieldErrors.spotReference && (
                  <small className="text-danger d-block">
                    {fieldErrors.spotReference}
                  </small>
                )}
              </div>
              <div className="col-md-6 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Strike %
                  </label>
                </div>
                <div className="input-group">
                  <NumberField
                    className="form-control"
                    value={form.strikePercent}
                    onChange={(e) => {
                      const val = e.target.value;
                      setValidatedValue('strikePercent', val, (v) =>
                        setForm((prev) => ({ ...prev, strikePercent: v }))
                      );
                      const pct = Number(val);
                      const spot = Number(form.spotReference);
                      if (
                        Number.isFinite(pct) &&
                        pct > 0 &&
                        Number.isFinite(spot) &&
                        spot > 0
                      ) {
                        setStrikePriceInput(
                          String((spot * (pct / 100)).toFixed(2))
                        );
                      }
                    }}
                    decimalPlaces={6}
                  />
                  <span className="input-group-text">%</span>
                </div>
                {fieldErrors.strikePercent && (
                  <small className="text-danger d-block">
                    {fieldErrors.strikePercent}
                  </small>
                )}
              </div>

              <div className="col-md-6 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Strike Price
                  </label>
                </div>
                <NumberField
                  className="form-control"
                  value={strikePriceInput}
                  onChange={(e) => handleStrikePriceChange(e.target.value)}
                  min={0}
                  decimalPlaces={2}
                />
              </div>
              <div className="col-12 nk-pps-field form-group">
                <div className="row g-2 align-items-end">
                  <div>
                    <ToggleButton
                      key={isBuzzerOn ? 'buzzer-on' : 'buzzer-off'}
                      isEnable={isBuzzerOn}
                      disableApi={true}
                      onToggleClick={() => setIsBuzzerOn((prev) => !prev)}
                    />
                  </div>
                  <div className="col-md-4">
                    <div className="form-label-group">
                      <label className="form-label iv-invest-lb-fixed">
                        Buffer %
                      </label>
                    </div>
                    <div className="input-group">
                      <NumberField
                        className="form-control"
                        value={buzzerPercent}
                        onChange={(e) =>
                          handleBufferPercentChange(e.target.value)
                        }
                        decimalPlaces={6}
                        disabled={!isBuzzerOn}
                      />
                      <span className="input-group-text">%</span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-label-group">
                      <label className="form-label iv-invest-lb-fixed">
                        Buffer Price
                      </label>
                    </div>
                    <NumberField
                      className="form-control"
                      value={buzzerAmount}
                      onChange={(e) => handleBufferPriceChange(e.target.value)}
                      decimalPlaces={2}
                      disabled={!isBuzzerOn}
                    />
                  </div>
                </div>
              </div>
              <div className="w-100"></div>

              <div className="col-md-4 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    KO Trigger %
                  </label>
                </div>
                <div className="input-group">
                  <NumberField
                    className="form-control"
                    value={form.koTrigger}
                    onChange={(e) => {
                      const val = e.target.value;
                      setValidatedValue('koTrigger', val, (v) =>
                        setForm((prev) => ({ ...prev, koTrigger: v }))
                      );
                      const pct = Number(val);
                      const spot = Number(form.spotReference);
                      if (
                        Number.isFinite(pct) &&
                        pct > 0 &&
                        Number.isFinite(spot) &&
                        spot > 0
                      ) {
                        setKoPriceInput(
                          String((spot * (pct / 100)).toFixed(2))
                        );
                      }
                    }}
                    decimalPlaces={6}
                  />
                  <span className="input-group-text">%</span>
                </div>
                {fieldErrors.koTrigger && (
                  <small className="text-danger d-block">
                    {fieldErrors.koTrigger}
                  </small>
                )}
              </div>

              <div className="col-md-4 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    KO Price
                  </label>
                </div>
                <NumberField
                  className="form-control"
                  value={koPriceInput}
                  onChange={(e) => handleKoPriceChange(e.target.value)}
                  min={0}
                  decimalPlaces={2}
                />
              </div>

              <div className="col-md-4 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Number of Guarentee Periods
                  </label>
                </div>
                <NumberField
                  className="form-control"
                  value={form.noKoPeriods}
                  onChange={(e) =>
                    setValidatedValue('noKoPeriods', e.target.value, (v) =>
                      setForm((prev) => ({ ...prev, noKoPeriods: v }))
                    )
                  }
                  min={0}
                  decimalPlaces={0}
                />
                {fieldErrors.noKoPeriods && (
                  <small className="text-danger d-block">
                    {fieldErrors.noKoPeriods}
                  </small>
                )}
              </div>

              <div className="col-md-4 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Purchase Amount 1
                  </label>
                </div>
                <TextField
                  className="form-control"
                  value={`${new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(
                    Number(displayNotional.purchaseAmount1) || 0
                  )} ${notionalCurrency}`}
                  disabled={true}
                />
                <small className="text-muted d-block mt-1">
                  {`≈ ${groupInteger(
                    displayNotional.buyAmount1
                  )} ${baseSpotAsset}`}
                </small>
              </div>

              <div className="col-md-4 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Purchase Amount 2
                  </label>
                </div>
                <TextField
                  className="form-control"
                  value={`${new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(
                    Number(displayNotional.purchaseAmount2) || 0
                  )} ${notionalCurrency}`}
                  disabled={true}
                />
                <small className="text-muted d-block mt-1">
                  {`≈ ${groupInteger(
                    displayNotional.buyAmount2
                  )} ${baseSpotAsset}`}
                </small>
              </div>

              <div className="col-md-4 nk-pps-field form-group">
                <div className="form-label-group">
                  <label className="form-label iv-invest-lb-fixed">
                    Purchase Amount Ratio
                  </label>
                </div>
                <NumberField
                  className="form-control"
                  value={form.boostedMultiplier}
                  onChange={(e) =>
                    setValidatedValue(
                      'boostedMultiplier',
                      e.target.value,
                      (v) =>
                        setForm((prev) => ({ ...prev, boostedMultiplier: v }))
                    )
                  }
                  min={1}
                  max={2}
                  decimalPlaces={2}
                />
                {fieldErrors.boostedMultiplier && (
                  <small className="text-danger d-block">
                    {fieldErrors.boostedMultiplier}
                  </small>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* <div className="col-lg-4">
        <section className="card card-bordered card-full">
          <div className="card-inner">
            <h5 className="card-title mb-3">Calculated Notional</h5>
            <div className="nk-ivp-summary card-text">
              <div className="row gy-2">
                <div className="col-12">
                  <span className="sub-text">
                    {String(form.fixingFrequency).toUpperCase().includes('WEEK')
                      ? 'Max Weekly Quantity'
                      : 'Max Monthly Quantity'}
                  </span>
                  <span className="lead-text text-dark">
                    {Math.round(displayNotional.maxQtyPerPeriod)}
                  </span>
                </div>
                <div className="col-12">
                  <span className="sub-text">Total Max Quantity</span>
                  <span className="lead-text text-dark">
                    {Math.round(displayNotional.totalMaxQuantity)}
                  </span>
                </div>
                <div className="col-12">
                  <span className="sub-text">Strike Price</span>
                  <span className="lead-text text-dark">
                    {formatCurrencyCode(displayNotional.strikePrice, spotReferenceCC)}
                  </span>
                </div>
                <div className="col-12">
                  <span className="sub-text">KO Price</span>
                  <span className="lead-text text-dark">
                    {formatCurrencyCode(displayNotional.koPrice, spotReferenceCC)}
                  </span>
                </div>
              </div>
            </div>
            <div className="nk-pps-field form-group mt-3">
              <div className="form-label-group">
                <label className="form-label iv-invest-lb-fixed">
                  Notional Amount
                </label>
              </div>
              <div className="form-control-group">
                <TextField
                  className="form-control form-control-lg bg-white"
                  value={formatCurrencyCode(displayNotional.totalNotional, spotReferenceCC)}
                  disabled={true}
                />
              </div>
            </div>
          </div>
        </section>
      </div> */}
      {/* <div className="col-12">
        <section className="card card-bordered card-full mt-3">
          <div className="card-inner">
            <h5 className="card-title mb-3">Fixing And Settlement Schedules</h5>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Fixing Date</th>
                    <th>Settlement Date</th>
                  </tr>
                </thead>
                <tbody>
                  {fixingDateLoading && (
                    <tr>
                      <td colSpan={2}>Loading...</td>
                    </tr>
                  )}
                  {!fixingDateLoading && fixingDateRows.length === 0 && (
                    <tr>
                      <td colSpan={2}>No fixing dates available.</td>
                    </tr>
                  )}
                  {!fixingDateLoading &&
                    pagedFixingRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.fixingDate || '-'}</td>
                        <td>{row.settlementDate || '-'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {!fixingDateLoading && fixingDateRows.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setFixingPage((p) => Math.max(1, p - 1))}
                  disabled={fixingPage === 1}
                >
                  Previous
                </button>
                <span>
                  Page {fixingPage} of {totalFixingPages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() =>
                    setFixingPage((p) => Math.min(totalFixingPages, p + 1))
                  }
                  disabled={fixingPage >= totalFixingPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>
      </div> */}
      <div className="col-12 text-center mt-3">
        <button
          className="btn btn-primary btn-lg"
          onClick={handleInvest}
          disabled={submitting}
        >
          {submitting ? 'Investing...' : 'Invest'}
        </button>
      </div>
    </div>
  );
};

export default Component;
